import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

/**
 * The document at the end of a link the customer was sent.
 *
 * <p>Nobody signs in here. The page fetches the file with the token in the URL
 * and shows it, so a phone opens it the way it opens any other PDF, and there
 * is a button for saving it rather than only a viewer that may or may not be
 * there.</p>
 */
export default function SharedDocumentPage() {
  const { token } = useParams<{ token: string }>()
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [fileName, setFileName] = useState('document.pdf')
  const [failed, setFailed] = useState<string | null>(null)

  useEffect(() => {
    let current: string | null = null
    let cancelled = false

    async function load() {
      try {
        const response = await fetch(`/api/v1/public/documents/${token}`)
        if (!response.ok) {
          setFailed(
            response.status === 404 || response.status === 422
              ? 'This link is not valid any more. Ask the office to send it again.'
              : 'The document could not be opened just now. Try again in a moment.',
          )
          return
        }
        const disposition = response.headers.get('Content-Disposition') ?? ''
        const match = /filename="?([^"]+)"?/.exec(disposition)
        if (match) setFileName(match[1])
        const url = URL.createObjectURL(await response.blob())
        current = url
        if (cancelled) URL.revokeObjectURL(url)
        else setBlobUrl(url)
      } catch {
        setFailed('The document could not be opened just now. Try again in a moment.')
      }
    }

    void load()
    return () => {
      cancelled = true
      if (current) URL.revokeObjectURL(current)
    }
  }, [token])

  if (failed) {
    return (
      <Box sx={{ p: 3, maxWidth: 560, mx: 'auto' }}>
        <Alert severity="warning">{failed}</Alert>
      </Box>
    )
  }

  if (!blobUrl) {
    return (
      <Stack sx={{ p: 6 }} alignItems="center" spacing={2}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Opening your document…
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack sx={{ height: '100vh' }}>
      <Stack direction="row" sx={{ p: 1 }} justifyContent="flex-end">
        <Button
          startIcon={<DownloadIcon />}
          href={blobUrl}
          download={fileName}
          variant="outlined"
          size="small"
        >
          Save
        </Button>
      </Stack>
      <Box
        component="iframe"
        title={fileName}
        src={blobUrl}
        sx={{ flexGrow: 1, border: 0, width: '100%' }}
      />
    </Stack>
  )
}
